import { useEffect, useState, createContext, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { formatDateTime } from "../lib/date";
import { toast } from "sonner";
import {
  ArrowLeft, BookOpen, Search, Eye, EyeOff, Save, Loader2,
  Server, Key, Users as UsersIcon, Plug, FileText, Database,
  Shield, CheckCircle, Clock, AlertTriangle, ExternalLink, Building2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Runbooks — per-client technical profile. Replaces the legacy Excel
 * `equipr_remote_support_template.xlsx`. Secrets are encrypted at rest
 * and masked by default; "Reveal" calls `?reveal=true` and is audited.
 */
export default function Runbooks() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [session, setSession] = useState(null);
  const [list, setList] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // full record
  const [infra, setInfra] = useState({ db: null, wcf: [], loading: false });   // Infrastructure cards
  const [revealed, setRevealed] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const raw = localStorage.getItem("equipr_session");
    if (!raw) { navigate("/login"); return; }
    setSession(JSON.parse(raw));
    fetchList();
  }, [navigate]);

  useEffect(() => {
    if (!query.trim()) { setFiltered(list); return; }
    const q = query.toLowerCase();
    setFiltered(list.filter(r =>
      (r.company_name || "").toLowerCase().includes(q) ||
      (r.client_id || "").toLowerCase().includes(q)
    ));
  }, [query, list]);

  // Deep-link support: `/runbooks?company=ABC%20Cranes` or `/runbooks?cid=ABC617`
  // auto-opens the matching runbook the first time the list resolves. Match is
  // forgiving: case-insensitive, normalised (no spaces/punct), longest common
  // prefix ≥ 5 chars wins. So JIRA org "ABCCRANES" finds "ABC Crane Hire".
  const [deepLinkConsumed, setDeepLinkConsumed] = useState(false);
  useEffect(() => {
    if (deepLinkConsumed || list.length === 0) return;
    const qCompany = searchParams.get("company");
    const qCid = searchParams.get("cid");
    if (!qCompany && !qCid) { setDeepLinkConsumed(true); return; }
    const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const lcp = (a, b) => {
      let i = 0;
      while (i < a.length && i < b.length && a[i] === b[i]) i++;
      return i;
    };

    let target = null;
    if (qCid) {
      target = list.find(r => (r.client_id || "").toUpperCase() === qCid.toUpperCase());
    }
    if (!target && qCompany) {
      const q = norm(qCompany);
      // 1) exact normalised match
      target = list.find(r => norm(r.company_name) === q);
      // 2) substring either-way
      if (!target) {
        target = list.find(r => {
          const n = norm(r.company_name);
          return n && (n.includes(q) || q.includes(n));
        });
      }
      // 3) longest-common-prefix scoring (handles "ABCCRANES" → "ABC Crane Hire")
      if (!target) {
        let best = null;
        let bestScore = 0;
        for (const r of list) {
          const score = lcp(norm(r.company_name), q);
          if (score > bestScore) { best = r; bestScore = score; }
        }
        if (bestScore >= 5) target = best;
      }
    }

    if (target) {
      const cid = target.client_id || (target.equipr || {}).client_id;
      openRunbook(cid, target.company_name, false, searchParams.get("edit") === "1");
      setQuery(qCompany || qCid || "");
    } else if (qCid) {
      // A client can exist without a HubSpot partner row, so a client ID we
      // cannot find in the list still opens its runbook (blank, ready to fill)
      // rather than dead-ending on an error.
      openRunbook(qCid, qCompany || qCid, false, searchParams.get("edit") === "1");
      setQuery(qCid);
    } else {
      toast.error(`No runbook matched "${qCompany}"`);
    }
    setDeepLinkConsumed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  const fetchList = async () => {
    setLoading(true);
    try {
      // /partners is HubSpot Type=Partner ⨝ local runbooks. HubSpot is the
      // source of truth; local runbook augments it with technical/secrets.
      const res = await axios.get(`${API}/runbooks/partners`);
      setList(res.data.partners || []);
    } catch (e) {
      toast.error("Failed to load partner list");
    } finally {
      setLoading(false);
    }
  };

  const openRunbook = async (clientId, companyName, withReveal = false, startInEdit = false) => {
    // For HubSpot-only partners (no local runbook yet) we still need an
    // identifier to PUT against; default to a slugified company name.
    const cid = clientId || (companyName || "").replace(/[^A-Za-z0-9]/g, "_").toUpperCase().slice(0, 24);
    try {
      let doc = null;
      try {
        const res = await axios.get(`${API}/runbooks/${cid}`, {
          params: withReveal ? { reveal: true } : {},
          headers: { "X-Actor": session?.id || "" },
        });
        doc = res.data;
      } catch (err) {
        if (err?.response?.status === 404) {
          // Bootstrap a blank runbook for this Partner so the UI can render +
          // the user can fill it in inline.
          doc = { client_id: cid, company_name: companyName || cid };
        } else {
          throw err;
        }
      }
      setSelected(doc);
      setRevealed(withReveal);
      if (startInEdit) {
        // "Edit" on the client record should land in edit mode, not make the
        // user find the button again. Masked passwords are cleared so a save
        // never writes the bullets back over the real secret.
        const d = JSON.parse(JSON.stringify(doc));
        if (d.allocate_login?.password === "••••••••") d.allocate_login.password = "";
        if (d.sms_provider?.password === "••••••••") d.sms_provider.password = "";
        setDraft(d);
        setEditMode(true);
      } else {
        setDraft(null);
        setEditMode(false);
      }
      // Fetch Infrastructure health (database + web services) linked to this
      // client. The reliable join key is client_id (matches DB config client_id
      // / WCF service_id); fall back to company name.
      const company = doc.company_name || companyName || "";
      const key = doc.client_id || company;
      if (key) {
        setInfra({ db: null, wcf: [], loading: true });
        Promise.allSettled([
          axios.get(`${API}/databases/company/${encodeURIComponent(key)}`),
          axios.get(`${API}/wcf/company/${encodeURIComponent(key)}`),
        ]).then(([dbRes, wcfRes]) => {
          const db = dbRes.status === "fulfilled" && dbRes.value.data?.found
            ? dbRes.value.data.health : null;
          const wcf = wcfRes.status === "fulfilled" && wcfRes.value.data?.found
            ? (wcfRes.value.data.services || []) : [];
          setInfra({ db, wcf, loading: false });
        });
      } else {
        setInfra({ db: null, wcf: [], loading: false });
      }
    } catch (e) {
      toast.error("Failed to load runbook");
    }
  };

  const toggleReveal = async () => {
    if (!selected) return;
    await openRunbook(selected.client_id, selected.company_name, !revealed);
    if (!revealed) toast.success("Secrets revealed — recorded in audit log");
  };

  const startEdit = () => {
    if (!selected) return;
    // Reload as masked, then build a draft. We don't want plaintext passwords
    // sitting in the draft state unless the user explicitly types a new one.
    const d = JSON.parse(JSON.stringify(selected));
    // Wipe masked password placeholders so we don't write "••••" back
    if (d.allocate_login?.password === "••••••••") d.allocate_login.password = "";
    if (d.sms_provider?.password === "••••••••") d.sms_provider.password = "";
    setDraft(d);
    setEditMode(true);
  };

  const saveDraft = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      // Strip empty-string passwords so we don't blow away the existing encrypted value
      const payload = JSON.parse(JSON.stringify(draft));
      if (payload.allocate_login && !payload.allocate_login.password) delete payload.allocate_login.password;
      if (payload.sms_provider && !payload.sms_provider.password) delete payload.sms_provider.password;
      await axios.put(`${API}/runbooks/${draft.client_id}`, payload, {
        headers: { "X-Actor": session?.id || "" },
      });
      toast.success("Runbook saved");
      setEditMode(false);
      await openRunbook(draft.client_id, draft.company_name, false);
      await fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save runbook");
    } finally {
      setSaving(false);
    }
  };

  const markReviewed = async () => {
    if (!selected) return;
    try {
      await axios.post(`${API}/runbooks/${selected.client_id}/mark-reviewed`, null, {
        headers: { "X-Actor": session?.id || "" },
      });
      toast.success("Marked as reviewed");
      await openRunbook(selected.client_id, selected.company_name, revealed);
      await fetchList();
    } catch (e) {
      toast.error("Failed to mark reviewed");
    }
  };

  const updateDraft = (path, value) => {
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = cur[parts[i]] || {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  // ───── render ─────
  return (
    <div className="min-h-screen bg-[#EDEAE3] text-[#1A1A1A]">
      <header className="border-b border-[#DAD5CA] px-6 py-4 flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/")}
          className="text-[#59554D] hover:text-[#1A1A1A] hover:bg-[#F4F2ED]"
          data-testid="runbooks-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
        </Button>
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-[#8A6D00]" />
          <h1 className="text-xl font-semibold">Client Runbooks</h1>
        </div>
        <span className="text-xs text-[#8A857A] ml-2">
          {list.length} client{list.length === 1 ? "" : "s"}
        </span>
      </header>

      <div className="flex" style={{ height: "calc(100vh - 65px)" }}>
        {/* List pane */}
        <aside className="w-96 border-r border-[#DAD5CA] flex flex-col">
          <div className="p-3 border-b border-[#DAD5CA]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A857A]" />
              <Input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search by name or client ID…"
                className="pl-9 bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]"
                data-testid="runbook-search"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-[#8A6D00]" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-[#8A857A] text-center py-8">No runbooks match.</p>
            ) : (
              filtered.map(r => {
                const eq = r.equipr || {};
                const cid = r.client_id || eq.client_id;
                const driftIcon = !r.runbook_linked && r.source === "hubspot";
                return (
                  <button
                    key={`${r.source}-${cid || r.company_name}`}
                    onClick={() => openRunbook(cid, r.company_name, false)}
                    className={`w-full text-left p-3 border-b border-[#F4F2ED] hover:bg-[#FFFFFF] transition-colors ${
                      selected?.client_id === cid ? "bg-[#FFFFFF]" : ""
                    }`}
                    data-testid={`runbook-item-${cid || r.company_name}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium truncate">{r.company_name}</span>
                      <div className="flex items-center gap-1 shrink-0">
                        {eq.sla && (
                          <span className="text-[10px] bg-[#F1EEE6] text-[#8A857A] border border-[#DAD5CA] px-1.5 py-0.5 rounded" title="SLA client">
                            SLA
                          </span>
                        )}
                        {driftIcon && (
                          <span
                            className="text-[10px] bg-[#FFF7D1] text-[#6B5600] border border-[#FFF7D1] px-1.5 py-0.5 rounded"
                            title="No local runbook yet — click to create"
                          >
                            New
                          </span>
                        )}
                        {r.source === "runbook_only" && (
                          <span
                            className="text-[10px] bg-[#FFE9E0] text-[#C23A00] border border-[#FFE9E0] px-1.5 py-0.5 rounded"
                            title="No HubSpot Partner match — check Type=Partner in CRM"
                          >
                            !HS
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#8A857A] mt-0.5">
                      {cid && <span>{cid}</span>}
                      {eq.ea_lic != null && <span>EA: {String(eq.ea_lic)}</span>}
                      {eq.eo_lic != null && <span>EO: {String(eq.eo_lic)}</span>}
                      {eq.monthlies && <span>${String(eq.monthlies)}</span>}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        {/* Detail pane */}
        <main className="flex-1 overflow-y-auto p-6">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-[#8A857A]">
              <BookOpen className="w-12 h-12 mb-3 text-[#DAD5CA]" />
              <p>Select a client to view their runbook</p>
            </div>
          ) : (
            <RunbookDetail
              record={editMode ? draft : selected}
              editMode={editMode}
              revealed={revealed}
              saving={saving}
              infra={infra}
              onToggleReveal={toggleReveal}
              onStartEdit={startEdit}
              onCancelEdit={() => { setEditMode(false); setDraft(null); }}
              onSave={saveDraft}
              onMarkReviewed={markReviewed}
              onChange={updateDraft}
            />
          )}
        </main>
      </div>
    </div>
  );
}

// ───── Detail subcomponent ─────
// Supplies edit state to FieldRow without prop-drilling through every call site.
const EditContext = createContext({ editMode: false, onChange: () => {} });

// Section + FieldRow are defined at module scope (NOT inside RunbookDetail) so
// their component identity stays stable across re-renders — otherwise every
// keystroke would remount the inputs and steal focus after one character.
function Section({ icon: Icon, title, children, accent = "#8A6D00", action }) {
  return (
    <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base text-[#1A1A1A] flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: accent }} />
          {title}
        </CardTitle>
        {action}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function FieldRow({ label, value, path, type = "text" }) {
  const { editMode, onChange } = useContext(EditContext);
  return (
    <div className="flex items-start gap-3">
      <Label className="text-xs text-[#59554D] w-32 pt-2 shrink-0">{label}</Label>
      {editMode ? (
        <Input
          type={type}
          value={value || ""}
          onChange={e => onChange(path, e.target.value)}
          className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-sm flex-1"
          data-testid={`field-${path}`}
        />
      ) : (
        <p className="text-sm text-[#1A1A1A] flex-1 pt-2 break-all">
          {value || <span className="text-[#8A857A]">—</span>}
        </p>
      )}
    </div>
  );
}

export function RunbookDetail({
  record, editMode, revealed, saving, infra,
  onToggleReveal, onStartEdit, onCancelEdit, onSave, onMarkReviewed, onChange,
}) {
  if (!record) return null;

  const updated = record.updated_at ? formatDateTime(record.updated_at) : null;
  const reviewed = record.last_reviewed_at ? formatDateTime(record.last_reviewed_at) : null;

  return (
    <EditContext.Provider value={{ editMode, onChange }}>
    <div className="max-w-4xl space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">{record.company_name}</h2>
          <p className="text-sm text-[#59554D] mt-0.5">
            {record.client_id}
            {record.sla && (
              <span className="ml-3 text-[10px] bg-[#E3F3E9] text-[#0E6B3F] border border-[#E3F3E9] px-1.5 py-0.5 rounded">
                SLA
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!editMode ? (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={onToggleReveal}
                className="border-[#DAD5CA] bg-transparent text-[#1A1A1A] hover:bg-[#F4F2ED]"
                data-testid="reveal-toggle"
              >
                {revealed ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                {revealed ? "Hide secrets" : "Reveal secrets"}
              </Button>
              <Button
                size="sm"
                onClick={onMarkReviewed}
                variant="outline"
                className="border-[#DAD5CA] bg-transparent text-[#1A1A1A] hover:bg-[#F4F2ED]"
                data-testid="mark-reviewed-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" /> Mark reviewed
              </Button>
              <Button
                size="sm"
                onClick={onStartEdit}
                className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A]"
                data-testid="edit-runbook-btn"
              >
                Edit
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={onCancelEdit}
                className="border-[#DAD5CA] bg-transparent text-[#1A1A1A]"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={onSave}
                disabled={saving}
                className="bg-[#0E6B3F] hover:bg-[#E3F3E9] text-[#1A1A1A]"
                data-testid="save-runbook-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Meta row */}
      <div className="flex flex-wrap gap-4 text-xs text-[#8A857A]">
        {updated && <span><Clock className="w-3 h-3 inline mr-1" /> Updated {updated} by {record.updated_by || "—"}</span>}
        {reviewed && <span><Shield className="w-3 h-3 inline mr-1" /> Reviewed {reviewed} by {record.last_reviewed_by || "—"}</span>}
      </div>

      {/* Infrastructure — Database & Web Service cards (from Infrastructure module) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Database card */}
        <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-[#1A1A1A] flex items-center gap-2">
              <Database className="w-4 h-4 text-[#8A6D00]" /> Database
            </CardTitle>
          </CardHeader>
          <CardContent>
            {infra?.loading ? (
              <p className="text-sm text-[#8A857A] flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Checking...
              </p>
            ) : !infra?.db ? (
              <p className="text-sm text-[#8A857A]" data-testid="runbook-db-none">
                No database linked to this client. Add one in Infrastructure → Databases (set its Company to match).
              </p>
            ) : (
              <div className="space-y-2" data-testid="runbook-db-card">
                <div className="flex items-center gap-2">
                  <StatusDot connected={infra.db.is_connected} status={infra.db.status} />
                  <span className="text-sm font-medium text-[#1A1A1A]">{infra.db.client_name}</span>
                </div>
                <InfraRow label="Host" value={infra.db.host} />
                <InfraRow label="Database" value={infra.db.database} />
                <InfraRow label="Status" value={infra.db.is_connected ? "Connected" : "Disconnected"} />
                <InfraRow label="Response" value={infra.db.response_time_ms != null ? `${Math.round(infra.db.response_time_ms)} ms` : "—"} />
                {infra.db.error_message && (
                  <p className="text-xs text-[#C23A00] mt-1">{infra.db.error_message}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Web Service card */}
        <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-[#1A1A1A] flex items-center gap-2">
              <Plug className="w-4 h-4 text-[#1D4ED8]" /> Web Service
            </CardTitle>
          </CardHeader>
          <CardContent>
            {infra?.loading ? (
              <p className="text-sm text-[#8A857A] flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Checking...
              </p>
            ) : !infra?.wcf?.length ? (
              <p className="text-sm text-[#8A857A]" data-testid="runbook-wcf-none">
                No web service linked to this client. Add one in Infrastructure → Web Services (set its Company to match).
              </p>
            ) : (
              <div className="space-y-3" data-testid="runbook-wcf-card">
                {infra.wcf.map((svc, idx) => (
                  <div key={idx} className="space-y-2 border-b border-[#F4F2ED] last:border-0 pb-3 last:pb-0">
                    <div className="flex items-center gap-2">
                      <StatusDot connected={svc.is_reachable ?? svc.is_healthy} status={svc.status} />
                      <span className="text-sm font-medium text-[#1A1A1A]">{svc.client_name || svc.service_id}</span>
                    </div>
                    <InfraRow label="URL" value={svc.service_url} mono />
                    <InfraRow label="Status" value={svc.status || (svc.is_reachable ? "Reachable" : "Unreachable")} />
                    <InfraRow label="Response" value={svc.response_time_ms != null ? `${Math.round(svc.response_time_ms)} ms` : "—"} />
                    {svc.error_message && (
                      <p className="text-xs text-[#C23A00]">{svc.error_message}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Licenses (local — kept for backward-compat, will defer to HubSpot above) */}
      <Section icon={UsersIcon} title="Licenses (local runbook)">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <FieldRow label="Allocate" value={record.licenses?.allocate} path="licenses.allocate" />
          <FieldRow label="Operator" value={record.licenses?.operator} path="licenses.operator" />
          <FieldRow label="Maintain" value={record.licenses?.maintain} path="licenses.maintain" />
        </div>
      </Section>

      {/* Remote access */}
      <Section icon={Server} title="Remote Access">
        <div className="space-y-3">
          <FieldRow label="Type" value={record.remote_access_type} path="remote_access_type" />
          <FieldRow label="Server" value={record.server} path="server" />
        </div>
      </Section>

      {/* Allocate login */}
      <Section icon={Key} title="Allocate Login (shared support creds)">
        <div className="space-y-3">
          <FieldRow label="Username" value={record.allocate_login?.username} path="allocate_login.username" />
          <FieldRow
            label="Password"
            value={record.allocate_login?.password}
            path="allocate_login.password"
            type={revealed || editMode ? "text" : "password"}
          />
          {!revealed && !editMode && (
            <p className="text-[10px] text-[#8A857A] ml-32 -mt-2">
              Click "Reveal secrets" above to view (audited)
            </p>
          )}
        </div>
      </Section>

      {/* MSP provider */}
      <Section icon={UsersIcon} title="MSP / IT Provider">
        <div className="space-y-3">
          <FieldRow label="Provider" value={record.msp_provider?.name} path="msp_provider.name" />
          <FieldRow label="Email" value={record.msp_provider?.email} path="msp_provider.email" />
          <FieldRow label="Phone" value={record.msp_provider?.phone} path="msp_provider.phone" />
        </div>
      </Section>

      {/* SMS provider */}
      <Section icon={Key} title="SMS Provider">
        <div className="space-y-3">
          <FieldRow label="Provider" value={record.sms_provider?.name} path="sms_provider.name" />
          <FieldRow label="Username" value={record.sms_provider?.username} path="sms_provider.username" />
          <FieldRow
            label="Password"
            value={record.sms_provider?.password}
            path="sms_provider.password"
            type={revealed || editMode ? "text" : "password"}
          />
        </div>
      </Section>

      {/* Integrations */}
      <Section icon={Plug} title="Integrations">
        <div className="space-y-3">
          <FieldRow label="Accounting" value={record.integrations?.accounting} path="integrations.accounting" />
          <FieldRow label="Payroll" value={record.integrations?.payroll} path="integrations.payroll" />
          <FieldRow label="File location" value={record.file_location} path="file_location" />
          <div className="flex items-center gap-3">
            <Label className="text-xs text-[#59554D] w-32">Uses Jotform</Label>
            {editMode ? (
              <select
                value={record.uses_jotform_eqapp ? "yes" : "no"}
                onChange={e => onChange("uses_jotform_eqapp", e.target.value === "yes")}
                className="bg-[#FFFFFF] border border-[#DAD5CA] rounded-md px-3 py-1.5 text-[#1A1A1A] text-sm"
                data-testid="field-uses_jotform_eqapp"
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            ) : (
              <p className="text-sm text-[#1A1A1A]">
                {record.uses_jotform_eqapp == null
                  ? <span className="text-[#8A857A]">—</span>
                  : (record.uses_jotform_eqapp ? "Yes" : "No")}
              </p>
            )}
          </div>
        </div>
      </Section>

      {/* Rules */}
      <Section icon={FileText} title="Line Item / Overtime Rules">
        {editMode ? (
          <textarea
            value={record.line_item_overtime_rules || ""}
            onChange={e => onChange("line_item_overtime_rules", e.target.value)}
            className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-md px-3 py-2 text-[#1A1A1A] text-sm h-28 resize-y"
            data-testid="field-rules"
          />
        ) : (
          <p className="text-sm text-[#1A1A1A] whitespace-pre-wrap break-words">
            {record.line_item_overtime_rules || <span className="text-[#8A857A]">—</span>}
          </p>
        )}
      </Section>

      {/* SQL procedures (read-only for now) */}
      {(record.sql_procedures?.length || 0) > 0 && (
        <Section icon={Database} title="SQL — Do Not Update">
          <div className="space-y-2">
            {record.sql_procedures.map((sp, idx) => (
              <div key={idx} className="bg-[#FFFFFF] rounded p-3">
                <p className="text-sm font-mono text-[#6B5600]">{sp.name}</p>
                {sp.details && <p className="text-xs text-[#59554D] mt-1">{sp.details}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Notes */}
      <Section icon={FileText} title="Notes">
        {editMode ? (
          <textarea
            value={record.notes || ""}
            onChange={e => onChange("notes", e.target.value)}
            className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-md px-3 py-2 text-[#1A1A1A] text-sm h-20 resize-y"
            data-testid="field-notes"
          />
        ) : (
          <p className="text-sm text-[#1A1A1A] whitespace-pre-wrap">
            {record.notes || <span className="text-[#8A857A]">—</span>}
          </p>
        )}
      </Section>
    </div>
    </EditContext.Provider>
  );
}

// Compact read-only row for the Infrastructure (Database / Web Service) cards.
function InfraRow({ label, value, mono }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-xs text-[#59554D] w-24 shrink-0 pt-0.5">{label}</span>
      <span className={`text-sm text-[#1A1A1A] flex-1 break-all ${mono ? "font-mono text-xs" : ""}`}>
        {value == null || value === "" ? <span className="text-[#8A857A]">—</span> : String(value)}
      </span>
    </div>
  );
}

// Status indicator dot: green (connected/healthy), red (down), amber (warning).
function StatusDot({ connected, status }) {
  const s = (status || "").toString().toLowerCase();
  let color = connected ? "#0E6B3F" : "#C23A00";
  if (s.includes("warn")) color = "#6B5600";
  else if (s.includes("healthy") || s.includes("reachable") || s.includes("ok")) color = "#0E6B3F";
  else if (s.includes("critical") || s.includes("down") || s.includes("unreachable") || s.includes("error")) color = "#C23A00";
  return <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }} />;
}
